import { useEffect, useRef, useState } from "react";
import SSRIcon from "@ingka/ssr-icon";
import pathStarFilled from "@ingka/ssr-icon/paths/star-filled";
import pathStar from "@ingka/ssr-icon/paths/star";
import { useDropdownClamp } from "../hooks/useDropdownClamp";
import styles from "./MultiSelect.module.css";

interface SingleSelectOption {
    value: string;
    label: string;
    tooltip?: string;
    disabled?: boolean;
    disabledTooltip?: string;
}

interface SingleSelectProps {
    label: string;
    options: SingleSelectOption[];
    value: string;
    onChange: (value: string) => void;
    /** Text to show below the selector (e.g., date range) */
    subtitle?: string;
    /** Tooltip text for the label */
    tooltip?: string;
    /** Currently favorited value */
    favorite?: string | null;
    /** Callback when favorite is toggled */
    onFavoriteChange?: (value: string | null) => void;
}

export function SingleSelect({
    label,
    options,
    value,
    onChange,
    subtitle,
    tooltip,
    favorite,
    onFavoriteChange,
}: SingleSelectProps) {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useDropdownClamp<HTMLDivElement>(isOpen);
    const [hoveredOption, setHoveredOption] = useState<string | null>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const selectedOption = options.find(o => o.value === value);
    const displayText = selectedOption?.label || value;
    const triggerTooltip = selectedOption?.tooltip || tooltip;

    const handleSelect = (optionValue: string) => {
        onChange(optionValue);
        setIsOpen(false);
    };

    return (
        <div className={styles.container} ref={containerRef}>
            <label className={styles.label} title={tooltip}>{label}</label>
            <button
                type="button"
                className={styles.trigger}
                onClick={() => setIsOpen(!isOpen)}
                aria-expanded={isOpen}
                title={triggerTooltip}
            >
                <span className={styles.triggerText}>{displayText}</span>
                <span className={styles.arrow}>{isOpen ? "▲" : "▼"}</span>
            </button>

            {isOpen && (
                <div className={styles.dropdown} ref={dropdownRef}>
                    <div className={styles.options}>
                        {options.map((option) => (
                            <div
                                key={option.value}
                                className={`${styles.option} ${styles.singleOption} ${option.disabled ? styles.disabledOption : ""}`}
                                onClick={() => !option.disabled && handleSelect(option.value)}
                                onMouseEnter={() => setHoveredOption(option.value)}
                                onMouseLeave={() => setHoveredOption(null)}
                                title={option.disabled ? option.disabledTooltip : option.tooltip}
                            >
                                <span className={styles.optionText}>{option.label}</span>
                                {option.value === value && (
                                    <span className={styles.checkmark}>✓</span>
                                )}
                                {onFavoriteChange && !option.disabled && (
                                    <button
                                        type="button"
                                        className={`${styles.favoriteBtn} ${favorite === option.value ? styles.favorited : ""} ${hoveredOption === option.value || favorite === option.value ? styles.visible : ""}`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onFavoriteChange(favorite === option.value ? null : option.value);
                                        }}
                                        title={favorite === option.value ? "Remove as default" : "Set as default"}
                                    >
                                        {favorite === option.value ? (
                                            <SSRIcon paths={pathStarFilled} className={styles.starIcon} />
                                        ) : (
                                            <SSRIcon paths={pathStar} className={styles.starIcon} />
                                        )}
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {subtitle && (
                <span className={styles.summary}>{subtitle}</span>
            )}
        </div>
    );
}
